/**
 * PaymentMethodOrderDialog — 调整全局策略的支付方式下发顺序（ADM-APP-S013 / A8 §7.8.2）
 *
 * 顺序是全局策略组 `payment_methods` 的数组位置，跨分组统一一条，所以这里把全部成员列成一个列表：
 * 上移 / 下移 / 拖拽改本地草稿，点「保存顺序」才整组 PUT 一次（不逐次落库）。拖拽只是鼠标快捷方式，
 * 键盘与触屏走上移 / 下移按钮。
 *
 * 保存走条件调序（§7.8.10）：只改顺序、不碰成员与图标，缺图标的存量组照常可调序；
 * 成员被他人改动时由服务端返回冲突，调用方负责提示并刷新。
 */

import { useState } from 'react';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { SortableList } from '~/ui/SortableList';
import {
  PaymentMethodOrderIconTile,
  type PaymentMethodOrderItem,
} from './PaymentMethodOrderPreview';
import { NOT_SELECTABLE_BADGE } from './payment-method-labels';

export function PaymentMethodOrderDialog({
  scopeLabel,
  items,
  saving,
  onSave,
  onClose,
}: {
  /** 策略组名（全局策略 / BR 覆盖…），进标题 */
  scopeLabel: string;
  /** 打开时的当前顺序 */
  items: PaymentMethodOrderItem[];
  saving: boolean;
  onSave: (order: string[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(items);
  const changed = draft.some((item, index) => item.method !== items[index]?.method);

  return (
    <Dialog
      title={`调整下发顺序 · ${scopeLabel}`}
      subtitle="自上而下即 C 端展示次序；上移 / 下移或拖拽，保存时整组替换。"
      icon="IconGrip"
      width={560}
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" disabled={saving} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !changed}
            onClick={() => onSave(draft.map((item) => item.method))}
          >
            {saving ? '保存中…' : '保存顺序'}
          </button>
        </>
      }
    >
      <DialogBody>
        <SortableList
          items={draft}
          getKey={(item) => item.method}
          itemLabel={(item) => item.label}
          onReorder={setDraft}
          disabled={saving}
          renderContent={(item) => (
            <div className="hstack" style={{ gap: 10 }}>
              <PaymentMethodOrderIconTile item={item} size={28} />
              <span style={{ fontSize: 12.5 }}>{item.label}</span>
              {item.unavailable && (
                <span className="chip chip-neg" style={{ fontSize: 10 }}>
                  Stripe 后台未启用
                </span>
              )}
              {item.notSelectable && (
                <span className="chip chip-neg" style={{ fontSize: 10 }}>
                  {NOT_SELECTABLE_BADGE}
                </span>
              )}
            </div>
          )}
        />
      </DialogBody>
    </Dialog>
  );
}
